import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/Layout';
import { Badge, Button, Card, ErrorNote, Input, Loading, Toggle } from '@/components/ui';
import { api } from '@/lib/api';
import { rk, useMe, useModels } from '@/lib/hooks';
import type { ModelRow } from '@/lib/types';

export const Models = () => {
  const me = useMe();
  const models = useModels();
  const admin = me.data?.role === 'admin';
  return (
    <>
      <PageHeader title="Models" subtitle="What agents may use, what each model accepts, and what it costs. Prices are USD per 1M tokens; without a price, cost shows as —." />
      <Card className="overflow-x-auto">
        {models.isLoading ? (
          <Loading />
        ) : models.error ? (
          <div className="p-4">
            <ErrorNote error={models.error} />
          </div>
        ) : (
          <table className="w-full min-w-[900px] text-[13px]">
            <thead>
              <tr className="border-b border-line text-left text-[11px] tracking-wide text-ink-3 uppercase">
                <th className="px-4 py-2 font-medium">Model</th>
                <th className="px-3 py-2 font-medium">Accepts</th>
                <th className="px-3 py-2 font-medium">Input $/1M</th>
                <th className="px-3 py-2 font-medium">Cached $/1M</th>
                <th className="px-3 py-2 font-medium">Output $/1M</th>
                <th className="px-3 py-2 font-medium">Enabled</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {models.data!.models.map((m) => (
                <ModelRowEditor key={m.id} m={m} admin={admin} />
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
};

const ModelRowEditor = ({ m, admin }: { m: ModelRow; admin: boolean }) => {
  const qc = useQueryClient();
  const [p, setP] = useState({ input: m.pricing?.input ?? '', cachedInput: m.pricing?.cachedInput ?? '', output: m.pricing?.output ?? '' });
  const [enabled, setEnabled] = useState(m.enabled);
  const dirty = String(p.input) !== String(m.pricing?.input ?? '') || String(p.output) !== String(m.pricing?.output ?? '') || String(p.cachedInput) !== String(m.pricing?.cachedInput ?? '') || enabled !== m.enabled;
  const save = useMutation({
    mutationFn: () => api(`/v1/models/${m.id}`, { method: 'PUT', body: { pricing: p, enabled } }),
    onSuccess: () => {
      toast.success(`${m.name} saved`);
      void qc.invalidateQueries({ queryKey: rk('models') });
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const c = m.capabilities;
  return (
    <tr className="border-b border-line-2 last:border-0">
      <td className="px-4 py-2">
        <div className="font-medium">{m.label ?? m.name}</div>
        <div className="mono text-ink-3">
          {m.provider}:{m.name}
        </div>
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-1">
          {c.temperature ? <Badge>temperature</Badge> : <Badge tone="warn" title="Temperature is dropped at runtime">no temperature</Badge>}
          {c.reasoningEfforts.length > 0 && <Badge>reasoning</Badge>}
          {c.toolsRequireReasoningNone && <Badge tone="warn" title="Reasoning is forced to 'none' when tools are attached">tools ⇒ no reasoning</Badge>}
          {c.files && <Badge>files</Badge>}
          {c.jsonSchema && <Badge>json schema</Badge>}
        </div>
      </td>
      {(['input', 'cachedInput', 'output'] as const).map((k) => (
        <td key={k} className="px-3 py-2">
          <Input className="h-7 w-24 tabular-nums" type="number" min={0} step="0.01" disabled={!admin} value={p[k] as string | number} onChange={(e) => setP({ ...p, [k]: e.target.value })} placeholder="—" />
        </td>
      ))}
      <td className="px-3 py-2">
        <Toggle disabled={!admin} checked={enabled} onChange={setEnabled} label="" />
      </td>
      <td className="px-3 py-2 text-right">{admin && dirty && <Button size="sm" variant="primary" loading={save.isPending} onClick={() => save.mutate()}>Save</Button>}</td>
    </tr>
  );
};
